import * as React from 'react';
import type {
  DspMutateWriteResult,
  SetLineItemTargetingToolInput
} from 'shared/src/api/ai-media-plan-chat-types';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';

export type BeeswaxTargetingCardProps = {
  toolCallId: string;
  input: SetLineItemTargetingToolInput;
  platformId: number;
  executionLineItemId: string;
  strategyId: string;
  /** Outcome of a resolution made outside this card (prior session, or the modal's soft-cancel). */
  externalOutcome?: CommitOutcome | null;
  onResolve: (output: DspMutateWriteResult) => void | Promise<void>;
};

const GROUP_LABELS: Record<string, string> = { all: 'Include', any: 'Match any', none: 'Exclude' };
const GROUP_COLORS: Record<string, string> = {
  all: 'bg-green-100 text-green-700',
  any: 'bg-amber-100 text-amber-700',
  none: 'bg-red-100 text-red-700'
};

/**
 * Confirm card for the Beeswax setLineItemTargeting confirmed write: summarizes the proposed targeting
 * modules and, on confirm, calls beeswaxSetLineItemTargeting (creates the expression + attaches it).
 * Live-tier commit card — it writes into Beeswax directly.
 */
export function BeeswaxTargetingCard({
  toolCallId,
  input,
  platformId,
  executionLineItemId,
  strategyId,
  externalOutcome,
  onResolve
}: BeeswaxTargetingCardProps): JSX.Element {
  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);
  const [successUrl, setSuccessUrl] = React.useState<string | null>(null);

  const mutation = trpc.beeswaxSetLineItemTargeting.useMutation();

  const rules = summarizeTargeting(input.targeting);

  async function handleConfirm() {
    try {
      const res = await mutation.mutateAsync({
        platform_id: platformId,
        tool_call_id: toolCallId,
        line_item_id: input.line_item_id,
        targeting: input.targeting,
        execution_line_item_id: executionLineItemId,
        strategy_id: strategyId
      });
      setOutcome({ status: 'applied', summary: 'Targeting set in Beeswax' });
      if (res.result?.url) setSuccessUrl(res.result.url);
      await onResolve({
        status: res.status,
        entity_type: res.result?.entity_type,
        entity_id: res.result?.entity_id,
        url: res.result?.url,
        summary: res.result?.summary,
        warnings: res.result?.warnings
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to set targeting';
      setOutcome({ status: 'failed', summary: msg });
      showErrorToast(msg);
      // Feed the error back so the agent can explain it and re-propose corrected targeting.
      await onResolve({ status: 'failed', error: msg });
    }
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    await onResolve({ status: 'cancelled' });
  }

  return (
    <>
      <CommitCardFrame
        tier="live"
        badgeLabel="Writes to Beeswax"
        title={`Set targeting${input.line_item_name ? ` · ${input.line_item_name}` : ''}`}
        consequence="Creates the targeting expression and attaches it to the Beeswax line item."
        confirmLabel="Set targeting"
        isPending={mutation.isPending}
        outcome={outcome}
        externalOutcome={externalOutcome ?? null}
        toolCallId={toolCallId}
        onConfirm={handleConfirm}
        onCancel={handleCancel}>
        <div className="mb-1 text-xs text-gray-500">Line item #{input.line_item_id}</div>

        {rules.length === 0 ? (
          <div className="text-sm text-gray-400">No targeting rules</div>
        ) : (
          <div className="space-y-1">
            {rules.map((r, i) => (
              <div key={`${r.key}-${i}`} className="flex items-center gap-2 text-sm">
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${GROUP_COLORS[r.group] ?? 'bg-gray-100 text-gray-600'}`}>
                  {GROUP_LABELS[r.group] ?? r.group}
                </span>
                <span className="font-medium text-gray-700">{r.key}</span>
              </div>
            ))}
          </div>
        )}
      </CommitCardFrame>

      {outcome?.status === 'applied' && successUrl ? (
        <div className="mt-2 px-1">
          <a
            href={successUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:underline">
            View in Beeswax &#x2197;
          </a>
        </div>
      ) : null}
    </>
  );
}

function summarizeTargeting(
  targeting: SetLineItemTargetingToolInput['targeting']
): Array<{ group: string; key: string }> {
  const out: Array<{ group: string; key: string }> = [];
  for (const [moduleKey, mod] of Object.entries(targeting.modules)) {
    for (const group of ['all', 'any', 'none'] as const) {
      const preds = (mod as Record<string, unknown>)[group];
      if (preds && typeof preds === 'object') {
        for (const k of Object.keys(preds as Record<string, unknown>)) {
          out.push({ group, key: `${moduleKey}.${k}` });
        }
      }
    }
  }
  return out;
}
